import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone } from "lucide-react";
import { CONTACT } from "@/lib/constants";
import { useMLO } from "@/lib/mloContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  loan_amount: "",
  loan_purpose: "Purchase",
  state: "",
  message: "",
  consent: true,
};

export default function LeadForm() {
  const mlo = useMLO();
  const full = mlo?.name || CONTACT.loanOfficer;
  const first = full.split(" ")[0];
  const phone = mlo?.phone || CONTACT.phoneDisplay;
  const phoneHref = mlo?.phone ? `tel:${mlo.phone.replace(/[^+0-9]/g, "")}` : CONTACT.phoneHref;

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to be contacted so we can send your rate.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, mlo_slug: mlo?.slug || null });
      setDone(true);
      toast.success(`Got it! ${first} will reach out shortly.`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-form-success"
        className="rounded-2xl border border-teal/30 bg-teal-light p-8 text-center"
      >
        <CheckCircle2 className="mx-auto text-teal" size={40} strokeWidth={1.5} />
        <h3 className="mt-4 font-heading font-medium text-2xl text-navy">
          Thanks — you're on {first}'s radar.
        </h3>
        <p className="mt-2 text-ink-600">
          Expect a call or email within one business day. Want to skip the wait?
        </p>
        <a
          href={phoneHref}
          data-testid="lead-form-call-cta"
          className="mt-5 inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3 rounded-full transition-colors"
        >
          Call {phone}
        </a>
      </div>
    );
  }

  return (
    <form
      data-testid="lead-form"
      onSubmit={submit}
      className="rounded-2xl bg-white border border-linen-border p-6 sm:p-8 shadow-soft"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading font-medium text-2xl text-navy">Prefer a call back?</h3>
          <p className="mt-1 text-sm text-ink-500">
            Share a few details and {first} will reach out with personalized rate options.
          </p>
        </div>
        <a
          data-testid="jason-call-cta"
          href={phoneHref}
          className="inline-flex items-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold px-5 py-2.5 rounded-full transition-colors text-sm"
        >
          <Phone size={16} /> Call {first} now
        </a>
      </div>
      <p className="mt-3 text-xs text-ink-500">
        Prefer to talk right now? Tap the button above or call{" "}
        <a href={phoneHref} className="text-navy font-semibold hover:text-teal underline decoration-linen-border underline-offset-2">
          {phone}
        </a>
        .
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name" required>
          <input
            data-testid="lead-form-name-input"
            required
            value={form.full_name}
            onChange={update("full_name")}
            type="text"
            placeholder="Jane Homeowner"
            className={inputCls}
          />
        </Field>
        <Field label="Email" required>
          <input
            data-testid="lead-form-email-input"
            required
            value={form.email}
            onChange={update("email")}
            type="email"
            placeholder="you@example.com"
            className={inputCls}
          />
        </Field>
        <Field label="Phone" required>
          <input
            data-testid="lead-form-phone-input"
            required
            value={form.phone}
            onChange={update("phone")}
            type="tel"
            placeholder="(555) 555-1234"
            className={inputCls}
          />
        </Field>
        <Field label="Estimated loan amount">
          <input
            data-testid="lead-form-amount-input"
            value={form.loan_amount}
            onChange={update("loan_amount")}
            type="text"
            placeholder="$450,000"
            className={inputCls}
          />
        </Field>
        <Field label="Loan purpose">
          <select
            data-testid="lead-form-purpose-select"
            value={form.loan_purpose}
            onChange={update("loan_purpose")}
            className={inputCls}
          >
            <option>Purchase</option>
            <option>Refinance</option>
            <option>Cash-out refinance</option>
            <option>Investment property</option>
            <option>Not sure yet</option>
          </select>
        </Field>
        <Field label="State">
          <input
            data-testid="lead-form-state-input"
            value={form.state}
            onChange={update("state")}
            type="text"
            placeholder="WA"
            maxLength={2}
            className={inputCls}
          />
        </Field>
        <Field label="Anything else? (optional)" className="sm:col-span-2">
          <textarea
            data-testid="lead-form-message-input"
            value={form.message}
            onChange={update("message")}
            rows={3}
            placeholder={`Tell ${first} about your goals or questions…`}
            className={`${inputCls} resize-none`}
          />
        </Field>
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-ink-600">
        <input
          data-testid="lead-form-consent-checkbox"
          type="checkbox"
          checked={form.consent}
          onChange={update("consent")}
          className="mt-1 h-4 w-4 rounded border-linen-border text-teal focus:ring-teal"
        />
        <span>
          I agree to be contacted by {full} / NEXA Lending about my inquiry.
          I understand my info won't be sold or shared.
        </span>
      </label>

      <button
        data-testid="lead-form-submit-button"
        type="submit"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light disabled:opacity-60 text-white font-semibold px-6 py-4 rounded-full transition-colors"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Sending…
          </>
        ) : (
          <>Request my rate consultation</>
        )}
      </button>
    </form>
  );
}

const inputCls =
  "w-full bg-linen/60 border border-linen-border rounded-xl px-4 py-3 text-sm text-navy placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal transition-all";

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-ink-600">
        {label} {required && <span className="text-teal">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
